/**
 * Bottle CSV import component
 */

import { useState, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Modal } from '../common';
import { csvService } from '../../services/csvService';
import { parseCSV, validateBottleCSV, resolveDistilleryNames, getBottleTemplate, downloadCSV } from '../../utils/csvParser';
import { useAllDistilleries } from '../../hooks/useDistilleries';
import { useToast } from '../../context/ToastContext';
import './BottleImport.css';

/**
 * Modal for importing bottles from CSV
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Whether modal is open
 * @param {Function} props.onClose - Close handler
 * @returns {React.ReactElement} Import modal element
 */
export default function BottleImport({ isOpen, onClose }) {
  const [validationResult, setValidationResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { data: distilleries = [], isLoading: distilleriesLoading } = useAllDistilleries();

  const handleFileSelect = async (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    setValidationResult(null);
    setImportResult(null);

    try {
      const { data, errors: parseErrors } = await parseCSV(selectedFile);

      if (parseErrors.length > 0) {
        setValidationResult({
          valid: false,
          errors: parseErrors.map(e => ({ row: e.row, errors: [e.message] })),
          validRows: []
        });
        return;
      }

      // Phase 1: Basic field validation
      const { validRows: basicValidRows, errors: basicErrors } = validateBottleCSV(data);

      // Phase 2: Distillery name resolution
      const { validRows: finalValidRows, errors: distilleryErrors } = resolveDistilleryNames(
        basicValidRows,
        distilleries
      );

      const allErrors = [...basicErrors, ...distilleryErrors];
      setValidationResult({ valid: allErrors.length === 0, validRows: finalValidRows, errors: allErrors });
    } catch (error) {
      addToast({ type: 'error', message: 'Failed to parse CSV file' });
    }
  };

  const handleImport = async () => {
    if (!validationResult?.validRows?.length) return;

    setImporting(true);
    try {
      const result = await csvService.importBottles(validationResult.validRows);
      setImportResult(result);

      if (result.imported > 0) {
        queryClient.invalidateQueries({ queryKey: ['bottles'] });
        queryClient.invalidateQueries({ queryKey: ['dashboard'] });
        addToast({
          type: 'success',
          message: `Successfully imported ${result.imported} bottles`
        });
      }
    } catch (error) {
      addToast({ type: 'error', message: error.message || 'Import failed' });
    } finally {
      setImporting(false);
    }
  };

  const handleDownloadTemplate = () => {
    downloadCSV(getBottleTemplate(), 'bottle_import_template.csv');
  };

  const handleClose = () => {
    setValidationResult(null);
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Import Bottles" size="lg">
      <div className="import-modal">
        <div className="import-modal__template">
          <p>Download the template CSV to see the expected format:</p>
          <Button variant="ghost" size="sm" onClick={handleDownloadTemplate}>
            Download Template
          </Button>
        </div>

        <div className="import-modal__upload">
          <label htmlFor="bottle-csv" className="import-modal__label">
            Select CSV File
          </label>
          <input
            ref={fileInputRef}
            id="bottle-csv"
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            disabled={distilleriesLoading}
            className="import-modal__input"
          />
        </div>

        {validationResult && (
          <div className="import-modal__validation">
            {validationResult.valid ? (
              <div className="import-modal__success">
                {validationResult.validRows.length} rows ready to import
              </div>
            ) : (
              <div className="import-modal__errors">
                <h4>Validation Errors ({validationResult.errors.length})</h4>
                <ul className="import-modal__error-list">
                  {validationResult.errors.slice(0, 10).map((err, i) => (
                    <li key={i}>
                      <strong>Row {err.row}:</strong> {err.errors.join('; ')}
                    </li>
                  ))}
                  {validationResult.errors.length > 10 && (
                    <li className="import-modal__more">
                      ... and {validationResult.errors.length - 10} more errors
                    </li>
                  )}
                </ul>
                {validationResult.validRows.length > 0 && (
                  <p className="import-modal__partial">
                    {validationResult.validRows.length} valid rows can still be imported
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {importResult && (
          <div className="import-modal__result">
            <p>
              <strong>Import Complete:</strong> {importResult.imported} bottles imported
            </p>
            {importResult.errors.length > 0 && (
              <div className="import-modal__import-errors">
                <p>{importResult.errors.length} batch errors occurred:</p>
                <ul className="import-modal__error-list">
                  {importResult.errors.map((err, i) => (
                    <li key={i}>
                      <strong>Batch {err.batch}:</strong> {err.message}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <Modal.Footer>
          <Button variant="ghost" onClick={handleClose}>
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {!importResult && validationResult?.validRows?.length > 0 && (
            <Button onClick={handleImport} loading={importing}>
              Import {validationResult.validRows.length} Bottles
            </Button>
          )}
        </Modal.Footer>
      </div>
    </Modal>
  );
}
